import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import Hls from "hls.js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Rend une image OU une vidéo MP4/HLS selon le format fourni.
 *
 * Priorité des sources vidéo :
 *   1. HLS adaptive bitrate (hls_master_url) si dispo → iOS lit nativement,
 *      Android via hls.js
 *   2. Variants MP4 720p/480p via <source> tags
 *   3. Fallback MP4 original
 *
 * Compatible iOS Safari (<10 attrs) / Android / Weixin / QQ.
 */
const FeaturedMedia = ({ item, url, title, testId }) => {
  const videoRef = useRef(null);
  const isVideo = /\.mp4($|\?)/i.test(url || "") || Boolean(item?.hls_master_url);
  const className =
    "w-full h-full object-cover transition-transform duration-300 group-hover:scale-105";

  // Attache hls.js si HLS dispo et navigateur non-Safari
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !item?.hls_master_url) return;
    const hlsUrl = BACKEND_URL + item.hls_master_url;
    // Safari (iOS/macOS) : HLS natif, pas besoin d'hls.js
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = hlsUrl;
      return;
    }
    // Chrome Android / Firefox : hls.js
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      return () => hls.destroy();
    }
    // Dernier recours : pas de HLS, fallback MP4 géré par <source>
  }, [item?.hls_master_url]);

  if (!isVideo) {
    return <img src={url} alt={title} className={className} loading="lazy" data-testid={testId} />;
  }

  const variants = item?.variants_urls || {};
  const poster = item?.poster_url ? (BACKEND_URL + item.poster_url) : undefined;
  const sources = [];
  if (variants["720p"]) sources.push({ url: variants["720p"], label: "720p" });
  if (variants["480p"]) sources.push({ url: variants["480p"], label: "480p" });
  if (url) sources.push({ url, label: "original" });

  return (
    <video
      ref={videoRef}
      className={className}
      muted
      autoPlay
      loop
      playsInline
      preload="metadata"
      controls={false}
      poster={poster}
      disablePictureInPicture
      // eslint-disable-next-line react/no-unknown-property
      webkit-playsinline="true"
      // eslint-disable-next-line react/no-unknown-property
      x5-playsinline="true"
      // eslint-disable-next-line react/no-unknown-property
      x5-video-player-type="h5-page"
      data-testid={testId}
    >
      {sources.map((s) => {
        const abs = s.url.startsWith("http") ? s.url : (BACKEND_URL + s.url);
        return <source key={s.label} src={abs} type="video/mp4" data-quality={s.label} />;
      })}
      Votre navigateur ne supporte pas la lecture vidéo HTML5.
    </video>
  );
};

export default function FeaturedCarousel({ autoPlayInterval = 5000 }) {
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/showcases/active`);
      setItems(response.data);
    } catch (error) {
      console.error("Error fetching featured content:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-play
  useEffect(() => {
    if (items.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [items.length, autoPlayInterval, isPaused]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  const handleItemClick = async (item) => {
    try {
      await axios.post(`${API}/showcases/${item.id}/click`);
    } catch (error) {
      console.error("Error tracking click:", error);
    }

    if (item.link_url) {
      window.open(item.link_url, "_blank");
    }
  };

  const trackView = useCallback(async (itemId) => {
    try {
      await axios.post(`${API}/showcases/${itemId}/view`);
    } catch (error) {
      console.error("Error tracking view:", error);
    }
  }, []);

  useEffect(() => {
    if (items[currentIndex]) {
      trackView(items[currentIndex].id);
    }
  }, [currentIndex, items, trackView]);

  if (isLoading) {
    return (
      <div className="w-full h-48 md:h-64 bg-gray-100 animate-pulse rounded-2xl"></div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  const currentItem = items[currentIndex];

  const getImageUrl = (item) => {
    if (item.image_url) {
      return BACKEND_URL + item.image_url;
    }
    return item.format === "banner" || item.format === "horizontal"
      ? "https://via.placeholder.com/1200x400/2563eb/ffffff?text=ARTEKNOS"
      : "https://via.placeholder.com/600x600/2563eb/ffffff?text=ARTEKNOS";
  };

  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl shadow-xl"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="featured-carousel"
    >
      {/* Main Slide */}
      <div
        className="relative cursor-pointer group"
        onClick={() => handleItemClick(currentItem)}
      >
        {(currentItem.format === "banner" || currentItem.format === "horizontal") ? (
          <div className="aspect-[3/1] md:aspect-[4/1] w-full">
            <FeaturedMedia
              item={currentItem}
              url={getImageUrl(currentItem)}
              title={currentItem.title}
              testId={`featured-image-${currentItem.id}`}
            />
          </div>
        ) : (
          <div className="aspect-square md:aspect-[2/1] w-full">
            <FeaturedMedia
              item={currentItem}
              url={getImageUrl(currentItem)}
              title={currentItem.title}
              testId={`featured-image-${currentItem.id}`}
            />
          </div>
        )}

        {/* Overlay with text */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent">
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-white">
            <h3 className="text-lg md:text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {currentItem.title}
            </h3>
            {currentItem.description && (
              <p className="text-sm md:text-base opacity-90 line-clamp-2">
                {currentItem.description}
              </p>
            )}
            {currentItem.link_url && (
              <span className="inline-block mt-2 text-sm bg-white/20 px-3 py-1 rounded-full hover:bg-white/30 transition-colors">
                En savoir plus
              </span>
            )}
          </div>
        </div>

        {/* Featured badge - neutral name */}
        <div className="absolute top-3 left-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
          Mise en avant
        </div>
      </div>

      {/* Navigation Arrows */}
      {items.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100"
            data-testid="featured-prev-btn"
          >
            <ChevronLeft className="w-5 h-5 text-gray-800" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100"
            data-testid="featured-next-btn"
          >
            <ChevronRight className="w-5 h-5 text-gray-800" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {items.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
          {items.map((item, index) => (
            <button
              key={item.id || `featured-dot-${index}`}
              onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white w-6'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              data-testid={`featured-dot-${index}`}
            />
          ))}
        </div>
      )}

      {/* Progress bar */}
      {items.length > 1 && !isPaused && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/30">
          <div
            className="h-full bg-white transition-all"
            style={{
              animation: `progress ${autoPlayInterval}ms linear infinite`,
              width: '100%'
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}
